import React, { useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { isTabletOrMobileSize } from "../../../utils/queries";

// Import icons but handle the TypeScript errors
import { FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";

import GymCard from "../../../components/GymCard";
import { sortGymsByCustomOrder } from "../../../utils/utils";
import {
  Container,
  TopContainer,
  Title,
  ScrollableContainer,
  BottomContainer,
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalGallery,
  GalleryImage,
  GalleryNavButton,
  ModalSection,
  SectionTitle,
  ModalContent,
  FeaturesList,
  FeatureItem,
  LocationInfo,
  PriceInfo,
  SelectButton,
  SoldOutButton,
  ModalFooter,
  CityTabsContainer,
  CityTab,
  GymCount,
  LoadingState,
  Subtitle,
  InlineDiscountBadge,
  RequirementBanner,
  AppLinkButton
} from "./styles";
import AmenitiesIconList from "../../../components/AmenitiesIconList";
import { Business } from "../../../utils/types";
import { addBasketProduct, setSelectedProduct } from "../../../slices/appSlice";
import { CHECKOUT, PAYMENT } from "../../../utils/routes";

// Define RootState interface for TypeScript
interface RootState {
  appData: {
    allBusiness: Business[];
    gymOwnerLoggedIn: boolean;
  };
  auth: {
    memberLoggedIn: boolean;
  };
}

// Props interface for the component
interface GymHopSectionProps {
  showSavings?: boolean;
  buttonText?: string;
  buttonAction?: "select" | "signup" | "scrolltop";
  backgroundColor?: string;
}


// Helper function to check if gym requires fundamentals course
const requiresFundamentalsCourse = (gymName: string): boolean => {
  return gymName === "Riptide CrossFit";
};

// ---- NEW: Availability helper derived from product.isActive / is_active ----
// Minimal shape to avoid tight coupling with backend typings
interface ProductLike { name?: string; isActive?: boolean; is_active?: boolean }

const getAvailabilityForProducts = (products: ProductLike[] | undefined) => {
  if (!products || products.length === 0) {
    // No product data provided. Treat as not sold out for safety; CTA will fall back to Select.
    return { allInactive: false, basicInactiveOnly: false } as const;
  }

  const byName: Record<string, ProductLike> = Object.fromEntries(
    products.map((p) => [String(p.name ?? "").toLowerCase(), p])
  );

  const isOn = (p?: ProductLike) => {
    if (!p) return false;
    // Accept either camelCase or snake_case flags
    if (typeof p.isActive === "boolean") return p.isActive;
    if (typeof p.is_active === "boolean") return p.is_active;
    return false;
  };

  const anyActive = products.some((p) => isOn(p));
  const allInactive = !anyActive;

  const basic = byName["basic"];
  const basicInactive = basic ? !isOn(basic) : true; // treat missing Basic as inactive for this UI rule

  const premiumActive = ["premium", "premium_quarterly", "premium_yearly"].some((n) => isOn(byName[n]));

  const basicInactiveOnly = basicInactive && premiumActive && !allInactive;

  return { allInactive, basicInactiveOnly } as const;
};
// -------------------------------------------------------------------------

const GymHopSection: React.FC<GymHopSectionProps> = ({
  showSavings = false,
  buttonText,
  buttonAction = "select",
  backgroundColor,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { allBusiness } = useSelector((state: RootState) => state.appData);
  const { gymOwnerLoggedIn } = useSelector((state: RootState) => state.appData);
  const { memberLoggedIn } = useSelector((state: RootState) => state.auth);
  const isTabletOrMobile = useMediaQuery({ query: isTabletOrMobileSize });

  const [selectedGym, setSelectedGym] = useState<Business | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [activeCity, setActiveCity] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Add ref for city tabs container
  const cityTabsRef = useRef<HTMLDivElement>(null);

  // Territory definitions — coordinate-based metro areas matching the mobile app
  const TERRITORIES = [
    { id: 'Miami',           lat: 25.7617, lon: -80.1918, radiusMi: 18,
      keywords: ['miami', 'brickell', 'wynwood', 'edgewater', 'coconut grove', 'hialeah', 'doral', 'midtown'] },
    { id: 'Coral Gables',   lat: 25.7215, lon: -80.2684, radiusMi: 10,
      keywords: ['coral gables'] },
    { id: 'Fort Lauderdale',lat: 26.1224, lon: -80.1373, radiusMi: 15,
      keywords: ['fort lauderdale', 'ft lauderdale', 'flagler village'] },
    { id: 'New York',       lat: 40.7128, lon: -74.0060, radiusMi: 35,
      keywords: ['new york', 'brooklyn', 'manhattan', 'tribeca', 'astoria', 'jersey', 'hoboken', 'englewood', 'bronx', 'queens'] },
    { id: 'Tampa',          lat: 27.9506, lon: -82.4572, radiusMi: 35,
      keywords: ['tampa', 'channelside', 'pinellas', 'st pete', 'clearwater'] },
  ];

  const haversine = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const assignTerritory = (gym: Business): string => {
    const addr = gym.address as any;
    const lat = parseFloat(addr?.latitude || '0');
    const lon = parseFloat(addr?.longitude || '0');

    // Prefer coordinate-based — find closest territory within radius
    if (lat && lon) {
      for (const t of TERRITORIES) {
        if (haversine(t.lat, t.lon, lat, lon) <= t.radiusMi) return t.id;
      }
    }

    // Keyword fallback
    const full = (addr?.fullAddress || '').toLowerCase();
    for (const t of TERRITORIES) {
      if (t.keywords.some(kw => full.includes(kw))) return t.id;
    }

    // Assign to nearest territory by coordinates so nothing falls into "Other"
    if (lat && lon) {
      let closest = TERRITORIES[0];
      let minDist = haversine(TERRITORIES[0].lat, TERRITORIES[0].lon, lat, lon);
      for (const t of TERRITORIES.slice(1)) {
        const d = haversine(t.lat, t.lon, lat, lon);
        if (d < minDist) { minDist = d; closest = t; }
      }
      return closest.id;
    }

    // Final fallback — default to first territory
    return TERRITORIES[0].id;
  };

  // Helper function to get gym pricing info
  const getGymPricing = (gym: Business) => {
    const jackedRabbitPrice = 75;
    const originalPrice = gym.actualPrice ?? null;

    if (!originalPrice || !showSavings) {
      return {
        jackedRabbitPrice,
        originalPrice: null as number | null,
        savings: 0,
        savingsPercent: 0
      };
    }

    const savings = originalPrice - jackedRabbitPrice;
    const savingsPercent = Math.round((savings / originalPrice) * 100);

    return {
      jackedRabbitPrice,
      originalPrice,
      savings,
      savingsPercent
    };
  };

  // Group gyms by territory using coordinate/keyword matching
  const gymsByCity = useMemo(() => {
    const grouped: Record<string, Business[]> = {};

    allBusiness.forEach((gym) => {
      const territory = assignTerritory(gym);
      if (!grouped[territory]) grouped[territory] = [];
      grouped[territory].push(gym);
    });

    Object.keys(grouped).forEach((t) => {
      grouped[t] = sortGymsByCustomOrder(grouped[t], "SWEAT440 Midtown Miami");
    });

    return grouped;
  }, [allBusiness]);

  // Show only territories that have at least one gym, in definition order
  const cities = useMemo(() =>
    TERRITORIES.map(t => t.id).filter(id => (gymsByCity[id]?.length ?? 0) > 0),
  [gymsByCity]);

  // Set default active city
  React.useEffect(() => {
    if (cities.length > 0 && !activeCity) {
      setActiveCity(cities[0]);
    }
  }, [cities, activeCity]);

  // Get current city's gyms
  const currentCityGyms = activeCity ? gymsByCity[activeCity] || [] : [];

  // Enhanced handleCityChange function with auto-scroll
  const handleCityChange = (city: string) => {
    if (city === activeCity) return;

    setIsLoading(true);
    setActiveCity(city);

    // Scroll the active tab into view on mobile
    if (cityTabsRef.current && window.innerWidth <= 768) {
      const activeButton = cityTabsRef.current.querySelector(`button[data-city="${city}"]`);
      if (activeButton) {
        (activeButton as HTMLElement).scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center"
        });
      }
    }

    // Add a subtle loading delay for better UX
    setTimeout(() => {
      setIsLoading(false);
    }, 150);
  };

  const handleGymClick = (gym: Business): void => {
    setSelectedGym(gym);
    setCurrentImageIndex(0);
    setShowModal(true);
  };

  const closeModal = (): void => {
    setShowModal(false);
    setSelectedGym(null);
  };

  const nextImage = () => {
    if (selectedGym?.gallery && Array.isArray(selectedGym.gallery) && selectedGym.gallery.length > 0) {
      setCurrentImageIndex((prevIndex) => (prevIndex === selectedGym.gallery.length - 1 ? 0 : prevIndex + 1));
    }
  };

  const prevImage = () => {
    if (selectedGym?.gallery && Array.isArray(selectedGym.gallery) && selectedGym.gallery.length > 0) {
      setCurrentImageIndex((prevIndex) => (prevIndex === 0 ? selectedGym.gallery.length - 1 : prevIndex - 1));
    }
  };

  // Helper function to get image URL from gallery item
  const getImageUrl = (item: any): string => {
    if (!item) return "";

    // If it's directly a string, return it
    if (typeof item === "string") return item;

    // If it's an object with specific properties
    if (item && typeof item === "object") {
      // Try different possible properties
      if ("imageUrl" in item && typeof item.imageUrl === "string") return item.imageUrl;
      if ("resourceUrl" in item && typeof item.resourceUrl === "string") return item.resourceUrl;
      if ("url" in item && typeof item.url === "string") return item.url;
      if ("src" in item && typeof item.src === "string") return item.src;
    }

    return "";
  };

  const handleSelectGym = () => {
    if (!selectedGym) return;

    const { allInactive, basicInactiveOnly } = getAvailabilityForProducts(selectedGym.products as any);

    if (allInactive) {
      closeModal();
      return;
    }

    if (basicInactiveOnly) {
      window.location.href = "https://apps.apple.com/us/app/jackedrabbit/id6747449627";
      return;
    }

    if (buttonAction === "scrolltop") {
      closeModal();
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    } else if (buttonAction === "signup") {
      navigate("/signup");
      closeModal();
      return;
    } else {
      // Original select logic
      const basicMembershipProduct = selectedGym.products?.find((p: any) => String(p.name ?? "").toLowerCase() === "basic");

      if (basicMembershipProduct) {
        dispatch(addBasketProduct(selectedGym));
        dispatch(setSelectedProduct(basicMembershipProduct));
        navigate(gymOwnerLoggedIn ? "/dashboard/gym_owners" : PAYMENT);
      }
    }

    closeModal();
  };

  // Get the current gym's pricing info for the modal
  const currentGymPricing = selectedGym ? getGymPricing(selectedGym) : null;

  return (
    <Container id="our-facilities" bg={backgroundColor}>
      <TopContainer bg={backgroundColor}>
        <Title>Start your first trial</Title>
        <Subtitle>Select one of the locations and find where you belong!</Subtitle>
      </TopContainer>

      {/* City Tabs */}
      <CityTabsContainer ref={cityTabsRef}>
        {cities.map((city) => (
          <CityTab key={city} active={city === activeCity} onClick={() => handleCityChange(city)} data-city={city}>
            {city}
            <GymCount>({gymsByCity[city]?.length || 0})</GymCount>
          </CityTab>
        ))}
      </CityTabsContainer>

      <ScrollableContainer>
        {isLoading ? (
          <LoadingState>Loading gyms...</LoadingState>
        ) : (
          <BottomContainer>
            {currentCityGyms.map((gymCard: Business) => {
              const pricing = getGymPricing(gymCard);

              return (
                <div key={gymCard?.businessId} style={{ position: "relative" }}>
                  {requiresFundamentalsCourse(gymCard.name) && (
                    <RequirementBanner>Requires a $130 fundamentals course</RequirementBanner>
                  )}
                  <GymCard gymCard={gymCard} onClick={() => handleGymClick(gymCard)} pricing={pricing} />
                </div>
              );
            })}
            {currentCityGyms.length === 0 && <LoadingState>No gyms available in {activeCity}</LoadingState>}
          </BottomContainer>
        )}
      </ScrollableContainer>

      {showModal && selectedGym && currentGymPricing && (
        <ModalOverlay onClick={closeModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selectedGym.name}</ModalTitle>
              <CloseButton onClick={closeModal}>
                <span style={{ display: "flex" }}>
                  {/* @ts-ignore */}
                  <FaTimes size={20} />
                </span>
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <ModalGallery>
                {selectedGym.gallery && Array.isArray(selectedGym.gallery) && selectedGym.gallery.length > 0 && (
                  <GalleryImage src={getImageUrl(selectedGym.gallery[currentImageIndex])} alt={`${selectedGym.name || ""} image`} />
                )}

                {selectedGym.gallery && Array.isArray(selectedGym.gallery) && selectedGym.gallery.length > 1 && (
                  <>
                   <GalleryNavButton left onClick={prevImage}> 
                    {/* @ts-ignore */} 
                    <span style={{ display: 'flex' }}><FaChevronLeft size={18} /></span> 
                    </GalleryNavButton> 
                    
                    <GalleryNavButton right onClick={nextImage}> 
                      {/* @ts-ignore */} 
                      <span style={{ display: 'flex' }}><FaChevronRight size={18} /></span> 
                      </GalleryNavButton>
                  </>
                )}
              </ModalGallery>

              <ModalContent>
                {isTabletOrMobile && (
                  <ModalFooter>
                    {(showSavings && currentGymPricing.originalPrice) || requiresFundamentalsCourse(selectedGym.name) ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center" }}>
                        {showSavings && currentGymPricing.originalPrice && (
                          <InlineDiscountBadge>
                            Save up to ${currentGymPricing.savings} ({currentGymPricing.savingsPercent}% off)
                          </InlineDiscountBadge>
                        )}
                        {requiresFundamentalsCourse(selectedGym.name) && (
                          <div
                            style={{
                              backgroundColor: "#dc2626",
                              color: "white",
                              fontSize: "11px",
                              fontWeight: "600",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              lineHeight: "1.2",
                              whiteSpace: "nowrap"
                            }}
                          >
                            Requires a $130 fundamentals course
                          </div>
                        )}
                      </div>
                    ) : null}

                    {(() => {
                      const { allInactive, basicInactiveOnly } = getAvailabilityForProducts(selectedGym?.products as any);
                      if (allInactive) return <SoldOutButton disabled>Sold Out</SoldOutButton>;
                      if (basicInactiveOnly)
                        return (
                          <AppLinkButton href="https://apps.apple.com/us/app/jackedrabbit/id6747449627" onClick={(e:any)=>e.stopPropagation()}>
                            Download the app to select this gym
                          </AppLinkButton>
                        );
                      return (
                        <SelectButton onClick={handleSelectGym}>
                          {buttonText || (buttonAction === "signup" ? "Create your free account" : "Select")}
                        </SelectButton>
                      );
                    })()}
                  </ModalFooter>
                )}

                <ModalSection>
                  <SectionTitle>About {selectedGym.name}</SectionTitle>
                  <p>
                    {selectedGym.description || `Experience premium fitness at ${selectedGym.name}. State-of-the-art equipment and expert trainers await you.`}
                  </p>
                </ModalSection>

                {/* Add fundamentals course info for Riptide CrossFit */}
                {requiresFundamentalsCourse(selectedGym.name) && (
                  <ModalSection>
                    <SectionTitle>Fundamentals Course Required</SectionTitle>
                    <p>
                      After your third session, you will need to pay a $130 fundamentals course directly with Riptide. This requirement applies to all memberships and ensures all members have the proper foundation and safety knowledge for CrossFit training.
                    </p>
                  </ModalSection>
                )}

                <ModalSection>
                  <SectionTitle>Features</SectionTitle>
                  <FeaturesList>
                    {selectedGym.amenities && selectedGym.amenities.map((feature, index) => <FeatureItem key={index}>{feature}</FeatureItem>)}
                  </FeaturesList>
                </ModalSection>

                <ModalSection>
                  <SectionTitle>Location</SectionTitle>
                  <LocationInfo>{selectedGym.address?.fullAddress}</LocationInfo>
                </ModalSection>
              </ModalContent>
            </ModalBody>

            {!isTabletOrMobile && (
              <ModalFooter>
                <div>
                  <PriceInfo>Save Money With JackedRabbit</PriceInfo>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px", justifyContent: "center" }}>
                    {showSavings && currentGymPricing.originalPrice && (
                      <InlineDiscountBadge>
                        Save up to ${currentGymPricing.savings} ({currentGymPricing.savingsPercent}% off)
                      </InlineDiscountBadge>
                    )}
                    {requiresFundamentalsCourse(selectedGym.name) && (
                      <div
                        style={{
                          backgroundColor: "#dc2626",
                          color: "white",
                          fontSize: "11px",
                          fontWeight: "600",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          lineHeight: "1.2",
                          whiteSpace: "nowrap"
                        }}
                      >
                        Requires a $130 fundamentals course
                      </div>
                    )}
                  </div>
                </div>

                {(() => {
                  const { allInactive, basicInactiveOnly } = getAvailabilityForProducts(selectedGym?.products as any);
                  if (allInactive) return <SoldOutButton disabled>Sold Out</SoldOutButton>;
                  if (basicInactiveOnly)
                    return (
                      <AppLinkButton href="https://apps.apple.com/us/app/jackedrabbit/id6747449627" onClick={(e:any)=>e.stopPropagation()}>
                        Download the app to select this gym
                      </AppLinkButton>
                    );
                  return (
                    <SelectButton onClick={handleSelectGym}>
                      {buttonText || (buttonAction === "signup" ? "Create your free account" : "Select")}
                    </SelectButton>
                  );
                })()}
              </ModalFooter>
            )}
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default GymHopSection;